import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Button } from '../../../../components/Button'
import { LineData } from '../../../../components/LineData'
import {
  LegalFormPresentational,
  type LegalFormData,
} from '../../../../components/Forms/LegalFormPresentational'
import { type ExpertDomain } from '../../../../types/domain/expert-domain'
import { useExpertLegalDataMutation } from '../../../../services/hooks/experts/mutations/useExpertLegalDataMutation'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../constants/toast'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import { yupResolver } from '@hookform/resolvers/yup'
import { legalPersonSchema } from '../../../../validators/legalPersonSchema'
import { formatCpf } from '../../../../utils/formatCpf'
import { capitalize } from '../../../../utils/capitalize'
import { normalizeCpf } from '../../../../utils/normalizeCpf'

interface LegalPersonEditViewProps {
  expert?: ExpertDomain
}

export const LegalPersonEditView = ({ expert }: LegalPersonEditViewProps) => {
  const [isFormVisible, setIsFormVisible] = useState(false)

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(legalPersonSchema),
    defaultValues: {
      name: expert?.name,
      phone: expert?.phone,
      email: expert?.email,
      cnpj: expert?.legalData?.cnpj,
      companyName: expert?.legalData?.companyName,
      linkedin: expert?.linkedin,
      addressNumber: expert?.naturalData?.addressNumber,
      birthdate: expert?.naturalData?.birthdate,
      rg: expert?.naturalData?.rg,
      cep: expert?.naturalData?.cep,
      street: expert?.naturalData?.street,
      complement: expert?.naturalData?.complement || '',
      neighborhood: expert?.naturalData?.neighborhood,
      city: expert?.naturalData?.city,
      state: expert?.naturalData?.state,
    },
  })

  const { formatMessage } = useIntl()
  const { mutate, isLoading } = useExpertLegalDataMutation()

  const handleCancel = () => {
    methods.reset()
    setIsFormVisible(false)
  }

  const handleSubmit = (data: LegalFormData) => {
    mutate(
      { id: expert!.id, data: { ...data, cpf: normalizeCpf(data.cpf) } },
      {
        onSuccess: () => {
          toast.success(
            formatMessage({ id: 'DATA_SUCCESSFULLY_SAVED' }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )
          setIsFormVisible(false)
        },
        onError: (error) => {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  if (isFormVisible) {
    return (
      <LegalFormPresentational
        {...methods}
        isSubmitting={isLoading}
        submitButtonText={formatMessage({ id: 'SAVE' })}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    )
  }

  return (
    <>
      <LineData label={'CNPJ'} text={expert?.legalData?.cnpj} />

      <LineData
        label={formatMessage({ id: 'COMPANY_NAME' })}
        text={expert?.legalData?.companyName}
      />

      <LineData label={'CPF'} text={expert?.cpf && formatCpf(expert?.cpf)} />

      <LineData
        label={formatMessage({ id: 'FULL_NAME' })}
        text={expert?.name}
      />
      <LineData
        label={formatMessage({ id: 'MEETING_LANGUAGES' })}
        text={
          expert?.meetingLanguages
            ?.map(
              (language) => language.charAt(0).toUpperCase() + language.slice(1)
            )
            .join(', ') || null
        }
      />

      <div className="flex flex-col md:flex-row md:gap-14">
        <LineData label={'E-mail'} text={expert?.email} />

        <LineData
          label={formatMessage({ id: 'TELEPHONE' })}
          text={expert?.phone}
        />
        <LineData
          label={formatMessage({ id: 'NATIONALITY' })}
          text={expert?.nationality ? capitalize(expert.nationality) : ''}
        />
      </div>

      <div className='flex flex-col md:flex-row gap-2'>
        <LineData
          label={formatMessage({ id: 'DATE_OF_BIRTH' })}
          text={expert?.naturalData?.birthdate}
        />

        <LineData label={'RG'} text={expert?.naturalData?.rg} />
      </div>

      <LineData label={'LinkedIn'} text={expert?.linkedin} />

      <LineData label={'CEP'} text={expert?.naturalData?.cep} />

      <LineData
        label={formatMessage({ id: 'STREET' })}
        text={expert?.naturalData?.street}
      />

      <div className="flex flex-col md:flex-row md:gap-14">
        <LineData
          label={formatMessage({ id: 'NUMBER' })}
          text={expert?.naturalData?.addressNumber}
        />
        <LineData
          label={formatMessage({ id: 'COMPLEMENT' })}
          text={expert?.naturalData?.complement}
        />
      </div>

      <div className="flex flex-col md:flex-row md:gap-14">
        <LineData
          label={formatMessage({ id: 'NEIGHBORHOOD' })}
          text={expert?.naturalData?.neighborhood}
        />
        <LineData
          label={formatMessage({ id: 'CITY' })}
          text={expert?.naturalData?.city}
        />
        <LineData
          label={formatMessage({ id: 'STATE' })}
          text={expert?.naturalData?.state}
        />
      </div>

      <div className="w-full md:w-fit mt-14">
        <Button type="button" fullWidth onClick={() => setIsFormVisible(true)}>
          {formatMessage({ id: 'EDIT_DATA' })}
        </Button>
      </div>
    </>
  )
}
